import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../../components/common/NotificationSystem';
import ProfessionalLoader from '../../components/common/ProfessionalLoader';
import { useAuth } from '../../hooks/useAuth';
import Logo from '../../components/common/Logo';
import './Auth.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useNotifications();
  const { login, logout, isAuthenticated, user, isLoading } = useAuth();

  // Check for existing session on component mount
  useEffect(() => {
    if (isAuthenticated && user) {
      // User is already authenticated, redirect to appropriate dashboard
      const userRole = user.user_role || user.role;
      if (userRole === 'Superuser' || userRole === 'Staff') {
        navigate('/admin-dashboard');
      } else {
        navigate('/user-dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Handle messages from location state
  useEffect(() => {
    // Check for message from location state
    if (location.state?.message) {
      showError(location.state.message);
    }
  }, [location, showError]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setErrors({});

    try {
      // Use useAuth hook for login
      const result = await login({
        email: formData.email,
        password: formData.password
      });

      if (result.success) {
        // Login successful - useAuth hook will handle navigation
        showSuccess('Login successful! Redirecting...', 'Welcome Back!');
      } else {
        // Handle login errors - check both error and message fields
        const errorMessage = result.error || result.message || 'Login failed. Please check your credentials.';

        if (errorMessage.includes('verify your email') || errorMessage.includes('email verification')) {
          setErrors({ general: 'Please verify your email address before logging in. Check your inbox for a verification link.' });
          showError('Email verification required', 'Verification Needed');
        } else if (errorMessage.includes('pending admin approval') || errorMessage.includes('approval')) {
          setErrors({ general: 'Your account is pending admin approval. Please contact an administrator.' });
          showError('Account pending approval', 'Approval Required');
        } else if (errorMessage.includes('Invalid credentials') || errorMessage.includes('Unable to log in')) {
          setErrors({ general: 'Invalid email or password. Please check your credentials.' });
          showError('Invalid credentials', 'Authentication Error');
        } else {
          setErrors({ general: errorMessage });
          showError(errorMessage, 'Authentication Error');
        }
      }
    } catch (error) {
      setErrors({ general: 'Login failed. Please try again.' });
      showError('Login failed', 'Authentication Error');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo
            className="auth-logo"
            alt="AFF Logo"
          />
          <h1>AFF Portal</h1>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
              autoComplete="email"
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
          </div>

          <div className="form-options">
            <Link to="/forgot-password" className="forgot-link">
              Forgot your password?
            </Link>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ProfessionalLoader size="small" variant="minimal" inline />
                Signing in...
              </div>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>Don't have an account? <Link to="/signup">Sign up here</Link></p>
        </div>
      </div>

    </div>
  );
};

export default Login;
